import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Building2, Maximize2, FileText, MapPin, User, Check, ExternalLink, Mail } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { EASE } from "@/components/motion/Reveal";

export function ProjectModal({ project, onClose }) {
  const { t, lang } = useLanguage();
  const d = t.references.detail;

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [project, onClose]);

  const meta = project
    ? [
        { icon: Building2, label: t.project.type, value: project.type[lang], id: "type" },
        { icon: FileText, label: d.scopeLabel, value: project.scope[lang], id: "scope" },
        { icon: Maximize2, label: t.references.area, value: project.area, id: "area" },
        { icon: MapPin, label: d.location, value: d.locationValue, id: "location" },
      ]
    : [];

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-6"
          data-testid="project-modal"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-navy-deep/80 backdrop-blur-sm"
            onClick={onClose}
            data-testid="project-modal-backdrop"
          />
          <motion.div
            initial={{ y: 48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 48, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative z-10 max-h-[92vh] w-full max-w-3xl overflow-y-auto bg-white shadow-2xl"
          >
            <div className="relative aspect-[16/8] overflow-hidden bg-navy-deep">
              <img src={project.image} alt={project.title[lang]} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/60 to-transparent" />
              <span className="absolute left-5 top-5 border border-white/25 bg-navy-deep/60 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white backdrop-blur-sm">
                {project.type[lang]}
              </span>
              <button
                onClick={onClose}
                data-testid="project-modal-close"
                aria-label="Close"
                className="absolute right-4 top-4 grid h-10 w-10 place-items-center bg-navy-deep/70 text-white backdrop-blur-sm transition-colors hover:bg-brand"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-7 md:p-10">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-navy/60">
                {project.area} — {project.scope[lang]}
              </p>
              <h3 className="font-display text-2xl font-bold tracking-tight text-navy md:text-3xl" data-testid="project-modal-title">
                {project.title[lang]}
              </h3>

              <div className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-2" data-testid="project-modal-meta">
                {meta.map((m) => (
                  <div key={m.id} data-testid={`project-modal-meta-${m.id}`} className="bg-white p-4">
                    <div className="mb-1.5 flex items-center gap-2 text-navy/50">
                      <m.icon className="h-4 w-4 text-brand" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.18em]">{m.label}</span>
                    </div>
                    <p className="text-sm font-semibold text-navy">{m.value}</p>
                  </div>
                ))}
                <div data-testid="project-modal-meta-responsible" className="bg-white p-4 sm:col-span-2">
                  <div className="mb-1.5 flex items-center gap-2 text-navy/50">
                    <User className="h-4 w-4 text-brand" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.18em]">{t.project.responsible}</span>
                  </div>
                  <p className="text-sm font-semibold text-navy">{project.responsible}</p>
                </div>
                <div data-testid="project-modal-processing" className="bg-white p-4 sm:col-span-2">
                  <div className="mb-1.5 flex items-center gap-2 text-navy/50">
                    <FileText className="h-4 w-4 text-brand" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.18em]">{d.processingLabel}</span>
                  </div>
                  <p className="text-sm font-semibold text-navy">{d.processingValue}</p>
                </div>
              </div>

              <div className="mt-9">
                <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-navy/60">{d.includedLabel}</p>
                <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="project-modal-included">
                  {d.included.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-navy/85">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={2.5} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-9">
                <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-navy/60">{d.documentsLabel}</p>
                <p className="text-base leading-relaxed text-muted-foreground" data-testid="project-modal-description">
                  {project.description[lang]}
                </p>
              </div>

              <div className="mt-9 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:flex-wrap">
                <a
                  href={project.detailsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-modal-documents-button"
                  className="group inline-flex items-center justify-center gap-2 bg-navy px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t.references.documents}
                </a>
                <a
                  href="#kontakt"
                  onClick={onClose}
                  data-testid="project-modal-contact-button"
                  className="group inline-flex items-center justify-center gap-2 border border-navy px-6 py-4 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
                >
                  <Mail className="h-4 w-4" />
                  {t.references.contact}
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
